// Style
import mainStyle from "../../configProvisioning.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isEqual, isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Components
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { RadioButton } from 'components/buttons/RadioButton';
import { Loading } from 'components/Loading';
import { StyledBox } from '../StyledBox';
import { HeaderIcon } from '../HeaderIcon';

// Global const
import { formLimitation } from "const";

// Local utils
import {
  getProfileRequestParams,
  getInitProfileList,
  getIsVlanInvalid,
  checkPvid,
  getInputStyles,
  getLinkspeedList,
} from "../../util";

const ContentAP = ({
  auth,
  alertInfo,
  setAlertInfo,
  defaultConfig,
  setDefaultConfig,
  selectedDrawerTarget,
  isOpenEditDrawer,
  showEditGeneral,
  closeCallback,
}) => {
  const [editUuids] = useState(() => ({
    getProfileList: uuidv4()
  }));

  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();

  const [isLoading, setIsLoading] = useState(false);
  const [isEdited, setIsEdited] = useState({});
  const [isChanged, setIsChanged] = useState(false);

  const [originalSelectedPort, setOriginalSelectedPort] = useState(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
  const [selectedPort, setSelectedPort] = useState(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
  const isNeedCombineModel = useMemo(() => selectedPort?.isNeedCombineModel, [selectedPort]);

  const [profileList, setProfileList] = useState([]);
  const selectedProfile = useMemo(() => profileList.find(profile => profile.isSelected), [profileList]);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault && !!selectedProfile?.value, [selectedProfile?.value]);

  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const { isCopper, isFiber } = useMemo(() => selectedPort, [selectedPort]);

  const [linkSpeeds, setLinkSpeeds] = useState({
    copperList: getLinkspeedList({ speedList: selectedPort?.supportedLinkSpeedList?.copperList || [] }),
    fiberList: getLinkspeedList({ speedList: selectedPort?.supportedLinkSpeedList?.fiberList || [] })
  });

  const { vlanIdsCount, isVlanInvalid } = useMemo(() => getIsVlanInvalid({ ports: defaultConfig.portConfigs, selectedPort }), [defaultConfig.portConfigs, selectedPort]);

  const isSelectedPortSupportPoE = useMemo(() => {
    const { poePorts, portIndex } = selectedPort;

    if (isEmpty(poePorts) || portIndex == null) return false;
    return poePorts[portIndex] !== 0;

  }, [selectedPort?.poePorts, selectedPort?.portIndex]);

  const getProfileList = () => {
    setIsLoading(true);
    const { apiUrl, requestParams } = getProfileRequestParams({ auth });

    sendRequest(
      apiUrl,
      editUuids.getProfileList,
      requestParams
    );
  };

  const onApply = () => {

    const { portConfigs } = defaultConfig;

    const editedPortConfigs = portConfigs.map(port => {
      let resultPort = {};

      if (isNeedCombineModel || port.portIndex === selectedPort.portIndex) {
        resultPort = {
          ...port,
          combinePortName: selectedPort.combinePortName,
          portName: isNeedCombineModel ? selectedPort.combinePortName : selectedPort.portName,
          portProfile: selectedPort.portProfile,
          PVID: selectedPort.PVID,
          allowVlan: selectedPort.allowVlan,
        }
      } else {
        resultPort = port;
      }

      return resultPort;
    })

    setDefaultConfig(prevConfig => {
      return {
        ...prevConfig,
        portConfigs: editedPortConfigs,
      }
    })

    closeCallback();
    setOriginalSelectedPort(selectedPort);
    setIsChanged(false);
  };

  const onCancel = () => {
    closeCallback();
  };

  const updatePorts = (field, value) => {
    setSelectedPort((prev) => ({
      ...prev,
      [field]: value
    }));

    if (!isEqual(originalSelectedPort[field], value)) {
      setIsEdited((prev) => ({ ...prev, [field]: true }));
    } else {
      setIsEdited((prev) => {
        const { [field]: removedField, ...rest } = prev;
        return rest;
      });
    }
  };

  const isFormInvalid = () => {
    let isInvalid = false;
    const { portName, portType, PVID, allowVlan } = selectedPort;

    if (portName === "") isInvalid = true;
    if (!checkPvid(PVID?.toString().trim())) isInvalid = true;
    if (isVlanInvalid) isInvalid = true;

    return isInvalid;
  };

  useEffect(() => {
    setOriginalSelectedPort(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
    setSelectedPort(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
  }, [selectedDrawerTarget]);

  useEffect(() => {
    if (!selectedPort || !originalSelectedPort) return;
    const isDiff = !isEqual(selectedPort, originalSelectedPort);
    setIsChanged(isDiff);
  }, [selectedPort, originalSelectedPort]);

  useEffect(() => {
    if (isOpenEditDrawer) {
      getProfileList();
      setIsEdited({});
    }
  }, [isOpenEditDrawer]);

  useEffect(() => {
    if (!selectedPort) return;

    const response = getData(editUuids.getProfileList);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the profile list.",
        });
      } else {
        setProfileList(getInitProfileList({
          profileList: responseData,
          selectedPort
        }));
      }

      setIsLoading(false);
    }
  }, [dataMap?.[editUuids.getProfileList], selectedPort]);

  if (!selectedPort) return <></>;

  return (
    <Box>
      {isLoading && <Loading />}

      <Box
        sx={{
          paddingLeft: "25px",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <>
          <HeaderIcon
            state={selectedPort?.state}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontWeight: theme.typography.customFontWeight[3],
                fontSize: theme.typography.customFontSize[5],
                color: theme.palette.primary.greyPrimary,
              }}
            >
              { isNeedCombineModel ? selectedPort?.combinePortName : selectedPort?.portName }
            </span>

            <Box sx={{ display: "flex", gap: 1 }}>
              Port { isNeedCombineModel ? selectedPort?.combinePortIndex : selectedPort?.portIndex }
            </Box>
          </Box>
        </>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          py: 2,
          px: 2,
          gap: "20px",
          height: "calc(100vh - 164px)",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            px: 2,
          }}
        >
          <CollapseCard title="Basic">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

              {/* Port Name */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Name
                </Typography>
                <TextField
                  type="text"
                  disabled={!showEditGeneral}
                  value={isNeedCombineModel ? selectedPort?.combinePortName : selectedPort?.portName}
                  onChange={e => {
                    if (isNeedCombineModel) updatePorts("combinePortName", e.target.value);
                    else updatePorts("portName", e.target.value);
                  }}
                  sx={getInputStyles({
                    theme,
                    isInValid: selectedPort?.portName === "",
                    isEdited,
                    field: "portName"
                  })}
                />
              </StyledBox>

              {/* Port Profile */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Profile
                </Typography>
                <MenuSelect
                  value={selectedProfile?.value || ""}
                  disabled={!showEditGeneral}
                  onChange={opt => {

                    setProfileList(prevProfileList => {
                      return prevProfileList.map(profile => {
                        return {
                          ...profile,
                          isSelected: profile.id === opt.id,
                        }
                      })
                    });

                    updatePorts("portProfile", opt.id);

                    // Replace by the profile value or not
                    if (!opt.isDefault) {
                      // Non default profile
                      Object.keys(opt).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          updatePorts(key, opt[key]);
                        }
                      });
                    } else {
                      // Default profile
                      Object.keys(originalSelectedPort).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          updatePorts(key, originalSelectedPort[key]);
                        }
                      });
                    }

                  }}
                  width="100%"
                  options={profileList}
                  isEditedField={isEdited.portProfile}
                />
              </StyledBox>

              {/* PVID */}
              <StyledBox>
                <Typography
                  className={mainStyle["collapse-typography"]}
                  sx={{ alignSelf: "flex-start", pt: "6px" }}
                >
                  PVID
                </Typography>

                {
                  isProfileMode &&
                  <TextField
                    disabled
                    type="number"
                    value={selectedProfile?.PVID ?? ""}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "PVID"
                    })}
                    helperText="(1~4094)"
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                  />
                }

                {
                  !isProfileMode &&
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable || !showEditGeneral}
                      type="number"
                      value={selectedPort?.PVID ?? ""}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                      onChange={e => updatePorts("PVID", e.target.value)}
                      onBlur={e => {
                        const num = Number(e.target.value);
                        if (!isNaN(num)) updatePorts("PVID", num);
                      }}
                      sx={getInputStyles({
                        theme,
                        isInValid: !checkPvid(selectedPort?.PVID?.toString().trim()),
                        isEdited,
                        field: "PVID"
                      })}
                    />
                  </Box>
                }
              </StyledBox>

              {
                !isProfileMode &&
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "-1.3rem" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

              {/* Allow VLAN */}
              {
                isProfileMode &&
                <StyledBox>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  <TextField
                    disabled
                    type="text"
                    placeholder="e.g. 1,4-7"
                    value={selectedProfile?.allowVlan}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "allowVlan"
                    })}
                    helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                  />

                </StyledBox>
              }

              {
                !isProfileMode &&
                <StyledBox>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable || !showEditGeneral}
                      type="text"
                      placeholder="e.g. 1, 4-7"
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`} // (1~4094)
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                      value={selectedPort?.allowVlan ?? ""}
                      onChange={e => updatePorts("allowVlan", e.target.value)}
                      sx={getInputStyles({
                        theme,
                        isInValid: isVlanInvalid,
                        isEdited,
                        field: "allowVlan"
                      })}
                    />
                  </Box>

                </StyledBox>
              }

              {
                !isProfileMode &&
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "-1.3rem" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

            </Box>
          </CollapseCard>

        </Box>

        {
          isChanged && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "16px",
                backgroundColor: "primary.lightBlue",
                mt: "auto",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
            >
              <OutlineButton
                sx={{ marginRight: "20px" }}
                width="100px"
                height="32px"
                onClick={onCancel}
              >
                Cancel
              </OutlineButton>
              <SolidButton
                width="100px"
                height="32px"
                disabled={isFormInvalid()}
                onClick={onApply}
              >
                Apply
              </SolidButton>
            </Box>
          )
        }
      </Box>
    </Box>
  );
};

export default ContentAP;